import React, { FC, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  FormControl,
  Select,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import type { RaceLogEntry } from '../../models/race.models';
import type { UpdateRaceLogPayload } from '../../models/race.models';
import { palette } from '../../theme';
import { getEquipmentPerformanceLabel, getRaceResultOrdinal, parseRaceDateTime } from '../../helpers/general.helpers';
import useSnackbarHelper from '../../hooks/snackbar.hook';

const PERFORMANCE_OPTIONS = [1, 2, 3, 4, 5].map((value) => ({
  value,
  label: getEquipmentPerformanceLabel(value),
}));

interface EditFormState {
  race_type: string;
  result: number | '';
  equipment_performance: number | '';
  race_note: string;
}

function initialEditForm(entry: RaceLogEntry): EditFormState {
  return {
    race_type: entry.race_type,
    result: entry.result,
    equipment_performance: entry.equipment_performance,
    race_note: entry.race_note ?? '',
  };
}

interface RaceLogEntryCardProps {
  entry: RaceLogEntry;
  onEdit?: (entryId: number, payload: UpdateRaceLogPayload) => void;
  onDelete?: (entryId: number) => void;
  onOpenTraining?: (trainingId: number) => void;
}

const RaceLogEntryCard: FC<RaceLogEntryCardProps> = ({ entry, onEdit, onDelete, onOpenTraining }) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<EditFormState>(() => initialEditForm(entry));
  const { showSnackbar } = useSnackbarHelper();

  const { dateDisplay, timeDisplay } = parseRaceDateTime(entry.race_date);
  const showMenu = Boolean(onEdit || onDelete);

  const handleRaceNameClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!entry.race_id || !onOpenTraining) return;
    onOpenTraining(entry.race_id);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleEditClick = () => {
    setEditForm(initialEditForm(entry));
    setIsEditing(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    onDelete?.(entry.id);
    setDeleteDialogOpen(false);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleSave = () => {
    if (!onEdit) return;
    if (editForm.result === '') {
      showSnackbar('Please fill in result.', 'warning');
      return;
    }
    if (editForm.equipment_performance === '') {
      showSnackbar('Please fill in equipment performance.', 'warning');
      return;
    }
    if (!editForm.race_type.trim()) {
      showSnackbar('Please enter race type.', 'warning');
      return;
    }
    onEdit(entry.id, {
      race_type: editForm.race_type.trim(),
      result: Number(editForm.result),
      equipment_id: null,
      equipment_performance: Number(editForm.equipment_performance),
      race_note: editForm.race_note.trim(),
    });
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const cardSx = {
    marginBottom: 2,
    padding: 1,
    backgroundColor: palette.background.subtle,
    borderRadius: 1,
    border: `1px solid ${palette.grey[200]}`,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr',
    gap: 2,
    alignItems: 'flex-start',
  };

  const cellSx = {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    textAlign: 'center' as const,
  };

  if (isEditing) {
    return (
      <Box sx={{ position: 'relative', mb: 2 }}>
        <Box sx={cardSx}>
          <Box sx={cellSx}>
            <Typography variant="subtitle2" sx={{ textAlign: 'center' }}>
              {entry.race_name}
            </Typography>
            <Typography variant="caption" display="block" sx={{ color: palette.text.secondary }}>
              Event
            </Typography>
          </Box>
          <Box sx={cellSx}>
            <TextField
              size="small"
              placeholder="e.g. 5km Mass Start"
              value={editForm.race_type}
              onChange={(e) => setEditForm((f) => ({ ...f, race_type: e.target.value }))}
              variant="outlined"
              sx={{ width: '100%' }}
            />
            <Typography variant="caption" display="block" sx={{ color: palette.text.secondary }}>
              Race
            </Typography>
          </Box>
          <Box sx={cellSx}>
            <Typography variant="subtitle2">{dateDisplay}</Typography>
            <Typography variant="caption" display="block" sx={{ color: palette.text.secondary }}>
              Date
            </Typography>
          </Box>
          <Box sx={cellSx}>
            <Typography variant="subtitle2">{timeDisplay}</Typography>
            <Typography variant="caption" display="block" sx={{ color: palette.text.secondary }}>
              Time of Day
            </Typography>
          </Box>
          <Box sx={cellSx}>
            <Typography variant="subtitle2">{entry.race_location}</Typography>
            <Typography variant="caption" display="block" sx={{ color: palette.text.secondary }}>
              Location
            </Typography>
          </Box>
          <Box sx={cellSx}>
            <TextField
              size="small"
              placeholder="e.g. 14"
              type="number"
              inputProps={{ min: 1 }}
              value={editForm.result === '' ? '' : editForm.result}
              onChange={(e) => {
                const v = e.target.value;
                setEditForm((f) => ({ ...f, result: v === '' ? '' : parseInt(v, 10) }));
              }}
              variant="outlined"
              sx={{ width: '100%' }}
            />
            <Typography variant="caption" display="block" sx={{ color: palette.text.secondary }}>
              Result
            </Typography>
          </Box>
          <Box sx={cellSx}>
            <FormControl size="small" variant="outlined" sx={{ width: '100%' }}>
              <InputLabel id={`equipment-performance-edit-${entry.id}`}>Ski Performance</InputLabel>
              <Select
                labelId={`equipment-performance-edit-${entry.id}`}
                label="Ski Performance"
                value={editForm.equipment_performance === '' ? '' : editForm.equipment_performance}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    equipment_performance: e.target.value === '' ? '' : Number(e.target.value),
                  }))
                }
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                {PERFORMANCE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" display="block" sx={{ color: palette.text.secondary }}>
              Ski Performance
            </Typography>
          </Box>
          <Box
            sx={{ minWidth: 0, gridColumn: '2 / -1', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
          >
            <TextField
              size="small"
              multiline
              minRows={3}
              placeholder="Ski performance notes..."
              value={editForm.race_note}
              onChange={(e) => setEditForm((f) => ({ ...f, race_note: e.target.value }))}
              variant="outlined"
              sx={{ width: '100%' }}
            />
          </Box>
          <Box sx={{ gridColumn: '1 / -1', mt: 1, display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={handleCancelEdit}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSave}>
              Save
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', marginBottom: 2 }}>
      {showMenu && (
        <>
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              zIndex: 1,
              padding: '2px',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            <MoreVertIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          </IconButton>
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {onEdit && (
              <MenuItem onClick={handleEditClick} sx={{ fontSize: 14 }}>
                <EditIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                Edit
              </MenuItem>
            )}
            {onDelete && (
              <MenuItem onClick={handleDeleteClick} sx={{ fontSize: 14, color: 'error.main' }}>
                <DeleteIcon sx={{ fontSize: 16, mr: 1 }} />
                Delete
              </MenuItem>
            )}
          </Menu>
        </>
      )}
      <Box sx={cardSx}>
        <Box sx={cellSx}>
          <Typography
            variant="subtitle2"
            sx={{
              cursor: entry.race_id && onOpenTraining ? 'pointer' : 'default',
              color: entry.race_id && onOpenTraining ? 'primary.main' : 'inherit',
              '&:hover': { textDecoration: entry.race_id && onOpenTraining ? 'underline' : 'none' },
            }}
            onClick={entry.race_id && onOpenTraining ? handleRaceNameClick : undefined}
          >
            {entry.race_name}
          </Typography>
          <Typography variant="caption" display="block" sx={{ color: palette.text.secondary }}>
            Event
          </Typography>
        </Box>
        <Box sx={cellSx}>
          <Typography variant="subtitle2">{entry.race_type}</Typography>
          <Typography variant="caption" display="block" sx={{ color: palette.text.secondary }}>
            Race
          </Typography>
        </Box>
        <Box sx={cellSx}>
          <Typography variant="subtitle2">{dateDisplay}</Typography>
          <Typography variant="caption" display="block" sx={{ color: palette.text.secondary }}>
            Date
          </Typography>
        </Box>
        <Box sx={cellSx}>
          <Typography variant="subtitle2">{timeDisplay}</Typography>
          <Typography variant="caption" display="block" sx={{ color: palette.text.secondary }}>
            Time of Day
          </Typography>
        </Box>
        <Box sx={cellSx}>
          <Typography variant="subtitle2">{entry.race_location}</Typography>
          <Typography variant="caption" display="block" sx={{ color: palette.text.secondary }}>
            Location
          </Typography>
        </Box>
        <Box sx={cellSx}>
          <Typography variant="subtitle2">{getRaceResultOrdinal(entry.result)}</Typography>
          <Typography variant="caption" display="block" sx={{ color: palette.text.secondary }}>
            Result
          </Typography>
        </Box>
        <Box sx={cellSx}>
          <Typography variant="subtitle2" fontWeight={700}>
            {getEquipmentPerformanceLabel(entry.equipment_performance)}
          </Typography>
          <Typography variant="caption" display="block" sx={{ color: palette.text.secondary }}>
            Ski Performance
          </Typography>
        </Box>
        <Box sx={{ minWidth: 0, gridColumn: '2 / -1' }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {entry.race_note}
          </Typography>
        </Box>
      </Box>

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle id="delete-race-log-dialog-title">Delete race log entry?</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-race-log-dialog-description">
            Are you sure you want to delete this race log entry? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RaceLogEntryCard;
